import { useEffect, useRef, useState } from 'react';
import {
    useMap,
} from '@vis.gl/react-google-maps';
import MapService, { IMapService } from '../services/MapService';
import { MapPolygon } from '../components/MapPolygon';
import MapPropertiesService from '../services/MapPropertiesService';
import { useMapBounds, useMapZoom, useMapDragStart, useMapDragEnd, useFeatureMouseOver, useFeatureMouseOut } from '../hooks/useMapStore';

interface MapFeaturesProps {
    focusOnSegmentId?: number;
    featureCollection: any;
    onChanges(change: any): void,
    onFeatureMouseOver(geometryId: number): void,
    onFeatureMouseOut(): void,
    onFeatureClick(geometryId: number): void
}

export const MapFeatures = ({
    focusOnSegmentId = 0,
    featureCollection,
    onChanges,
    onFeatureMouseOver,
    onFeatureMouseOut,
    onFeatureClick
}: MapFeaturesProps) => {

    const map = useMap();

    const mapService: IMapService = new MapService();
    const mapProperties = MapPropertiesService.properties;

    const stateBounds = useMapBounds();
    const stateZoom = useMapZoom();
    const stateDragStart = useMapDragStart();
    const stateDragEnd = useMapDragEnd();
    const stateFeatureMouseOver = useFeatureMouseOver();
    const stateFeatureMouseOut = useFeatureMouseOut();

    const componentRef: {
        current: {
            isBusy: boolean,
            hasCancelled: boolean,
            segmentIds: number[],
            mapChangeTimer: NodeJS.Timeout | null,
        }
    } = useRef({
        isBusy: false,
        hasCancelled: false,
        segmentIds: [],
        mapChangeTimer: null,
    });
    const { current: localRef } = componentRef;

    const [features, setFeatures] = useState<any[]>([]);
    const [mouseOverId, setMouseOverId] = useState(0);
    const [focusId, setFocusId] = useState(focusOnSegmentId);

    const getChanges = () => {
        if (!map) return;
        const segmentIds: number[] = [];
        const bounds: any = map.getBounds();

        for (const feature of features) {
            let hasFeature = false;
            for (const paths of feature.paths) {
                for (const path of paths) {
                    const latLng = new google.maps.LatLng(path.lat, path.lng);
                    if (bounds.contains(latLng)) {
                        hasFeature = true;
                        break;
                    }
                }
                if (hasFeature) break;
            }

            if (hasFeature) {
                segmentIds.push(feature.properties.segmentId);
            }
        }
        onChanges({ segmentIds: segmentIds });
    }
        
    useEffect(() => {
        localRef.hasCancelled = true;
        addSegmentsAsync();
    }, [stateBounds, stateZoom, stateDragEnd]);

    
    useEffect(() => {
        localRef.hasCancelled = true;
    }, [stateDragStart])
    
    useEffect(() => {
        setMouseOverId(stateFeatureMouseOver);
    }, [stateFeatureMouseOver]);
    
    useEffect(() => {
        if (!stateFeatureMouseOver) return;
        setMouseOverId(0);
    }, [stateFeatureMouseOut]);

    useEffect(() => {
        return (() => {
            mapService.removeFeatures(map, "featureType", mapProperties.SegmentFeatureType);
            setFeatures([]);
            // setNewFeatures([]);
        });
    }, []);

    useEffect(() => {
        if (!map) return;
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, featureCollection]);

    useEffect(() => {
        if (!map) return;
        onFeatureFocus(focusOnSegmentId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, focusOnSegmentId]);

    useEffect(() => {
        getChanges();
    }, [features])

    return (
        features.map((feature: any, index: number) =>
            <MapPolygon
                ref={null}
                key={index}
                source={feature}
                onMouseOver={onFeatureMouseOver}
                onMouseOut={onFeatureMouseOut}
                onClick={onFeatureClick}
                mouseOverId={mouseOverId}
                focusId={focusId}
            />
        )
    );

    async function initialize() {
        if (!featureCollection) return;

        for (const feature of featureCollection.features) {
            mapService.setFeatureGeometryAndBounds(feature);
        }
        await addSegmentsAsync();
    }

    async function addSegmentsAsync() {
        if (!map || !featureCollection) return;
        localRef.hasCancelled = false;

        const bounds = map.getBounds();
        if (!bounds) return;

        const dataFeatures: any[] = [];
        
        for (const feature of featureCollection.features) {
            if (localRef.hasCancelled) break;
            feature.properties.featureType = mapProperties.SegmentFeatureType;
            feature.properties.isVisible = true;
            feature.properties.isSelected = false;

            dataFeatures.push(feature);
        }

        setFeatures(dataFeatures);
    }

    function onFeatureFocus(focusId: number) {
        if (!map) return;

        setFocusId(focusId);
    }
}
